import { useEffect, useRef, useState } from 'react';

/**
 * ⭐⭐ CLOUDFLARE TURNSTILE FOR SUPABASE AUTH (owner, 2026-10-04).
 *
 * Supabase's CAPTCHA protection is ONE switch for the whole project — sign-up, password
 * sign-in and the confirmation re-send all require a token once it is on — so every
 * login screen sends one: this app's AuthPanel and the admin console's Login. This code
 * ships FIRST; the owner turns the switch on only after it is live (Supabase ignores a
 * token while the switch is off).
 *
 * The same widget as the marketing forms (Managed — most people see only a tick). One
 * site key everywhere, including localhost and the iOS app (`capacitor://localhost`):
 * Supabase verifies with ONE secret, so every token must come from this widget, and
 * `localhost` is on its hostname list (Cloudflare permits it; owner's call).
 */
export const TURNSTILE_SITE_KEY = '0x4AAAAAAFNWDVpPu98HJ4s3';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

type Turnstile = {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
};
declare global {
  interface Window { turnstile?: Turnstile }
}

let scriptPromise: Promise<void> | null = null;
function loadTurnstile(): Promise<void> {
  if (typeof window === 'undefined' || window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      s.onload = () => resolve();
      s.onerror = () => { scriptPromise = null; reject(new Error('turnstile script failed')); };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

/**
 * A Turnstile token for one auth request, and the widget that produces it.
 * `screen` — when the widget moves to a different screen (form ↔ "check your email"),
 * its box is a new element, so the widget is rendered again there (and the old token,
 * minted for the other screen, is dropped).
 */
export function useTurnstile(screen = 'default') {
  const [token, setToken] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setToken(null);
    loadTurnstile()
      .then(() => {
        if (cancelled || !boxRef.current || !window.turnstile) return;
        widgetId.current = window.turnstile.render(boxRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          // ⚠️ 'flexible' has a 300px minimum and pushed a 342px phone sideways on the
          // marketing site (measured) — narrower boxes take 'compact'.
          size: boxRef.current.clientWidth >= 300 ? 'flexible' : 'compact',
          // ⚠️ Not 'auto': that follows the DEVICE setting and drew a white box on this
          // always-dark page on a light-mode iPhone (simulator, 2026-10-04).
          theme: 'dark',
          callback: (t: string) => { setToken(t); setFailed(false); },
          'expired-callback': () => setToken(null),
          'error-callback': () => { setToken(null); setFailed(true); },
        });
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current);
      widgetId.current = null;
    };
  }, [screen]);

  /** Tokens are single-use: after every auth attempt, ask for a fresh one. */
  const reset = () => {
    setToken(null);
    if (widgetId.current && window.turnstile) window.turnstile.reset(widgetId.current);
  };

  const widget = (
    <div className="space-y-1">
      <div ref={boxRef} data-testid="turnstile" className="min-h-[65px]" />
      {failed && (
        <p role="alert" className="text-xs text-red-400">
          The security check didn&apos;t complete. You can still try — if it&apos;s required, refresh the page and try again.
        </p>
      )}
    </div>
  );

  /* ⚠️⚠️ A FAILED CHECK DOES NOT LOCK ANYONE OUT (owner, 2026-10-04). `ready` once there is
     a token — OR once the widget has errored / could not load (e.g. Cloudflare's 600010
     "challenge failure" in an automated browser, a blocked script). The request then goes
     WITHOUT a token and Supabase decides: while CAPTCHA protection is off it signs in as
     before; once on, Supabase itself refuses a token-less request. Gating on the token
     alone would have locked people out even with the switch off. */
  return { token, ready: !!token || failed, reset, widget };
}
